import React, { Suspense } from 'react'
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom'
import Header from './components/Header'
import Footer from './components/Footer'
import ScrollToTop from './components/ScrollToTop'
import LoadingSpinner from './components/LoadingSpinner'
import { SpeedInsights } from '@vercel/speed-insights/react';
import DocumentTitle from './components/DocumentTitle';
const TermsAndConditions = React.lazy(() => import('./pages/TermsAndConditions'))
const CookiePolicy = React.lazy(() => import('./pages/CookiePolicy.tsx'))
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'))
import NotFound from './pages/NotFound'
const ProductDetail = React.lazy(() => import('./pages/ProductDetail'))
const CourseDetailPage = lazyWithPreload(() => import('./pages/CourseDetailPage'))
const WorkshopDetail = React.lazy(() => import('./pages/WorkshopDetail'))
import HomePage from './pages/HomePage'
import { lazyWithPreload, preloadWhenIdle } from './utils/lazyWithPreload'
const Courses = lazyWithPreload(() => import('./pages/Courses'))
const FashionBusinessMasterclass = React.lazy(() => import('./pages/FashionBusinessMasterclass'))
const EcommerceMastery = React.lazy(() => import('./pages/EcommerceMastery'))
const EcommerceMasteryVirtual = React.lazy(() => import('./pages/EcommerceMasteryVirtual'))
const EnglishMastery = React.lazy(() => import('./pages/EnglishMastery'))
const UGCCreationMasterclass = React.lazy(() => import('./pages/UGCCreation'))
import { Analytics } from "@vercel/analytics/react"
import { useState, useEffect } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
const SuccessPage = React.lazy(() => import('./pages/PaymentResult').then(m => ({ default: m.SuccessPage })))
const FailurePage = React.lazy(() => import('./pages/PaymentResult').then(m => ({ default: m.FailurePage })))
const CancelPage = React.lazy(() => import('./pages/PaymentResult').then(m => ({ default: m.CancelPage })))
const PaymentTest = React.lazy(() => import('./pages/PaymentTest'))
const FormTest = React.lazy(() => import('./pages/FormTest'))
const Welcome = React.lazy(() => import('./pages/Welcome'))
const NotRegistered = React.lazy(() => import('./pages/NotRegistered'))
const PaymentRequired = React.lazy(() => import('./pages/PaymentRequired'))
const RegisterClarity = React.lazy(() => import('./pages/RegisterClarity'))
const PaymentTestPage = React.lazy(() => import('./pages/PaymentTestPage'))
const TestPayment = React.lazy(() => import('./pages/TestPayment'))
const WorkshopPaymentSuccess = React.lazy(() => import('./pages/WorkshopPaymentSuccess'))
import WhatsAppWidget from './components/WhatsAppWidget.tsx'
const VirtualTestPayment = React.lazy(() => import('./pages/virtualTestPayment'))
const WhatsAppWorkshop = React.lazy(() => import('./pages/WhatsAppWorkshop'))
const FirstSaleWorkshop = React.lazy(() => import('./pages/FirstSaleWorkshop'))
const BackToBusinessWorkshop = React.lazy(() => import('./pages/BackToBusinessWorkshop'))
const BackToBusinessWorkshopSept = React.lazy(() => import('./pages/BackToBusinessWorkshopSept'))
import { useLocation } from 'react-router-dom'

function TrackFacebookPageViews() {
  const location = useLocation()
  useEffect(() => {
    if (typeof fbq === 'function') fbq('track', 'PageView')
  }, [location.pathname, location.search])
  return null
}
// Lazy load the pages
const Blog = React.lazy(() => import('./pages/Blog'))
const Community = React.lazy(() => import('./pages/Community'))
const Careers = React.lazy(() => import('./pages/Careers'))
const ContactUs = React.lazy(() => import('./pages/ContactUs'))
const Consultation = lazyWithPreload(() => import('./pages/Consultation'))
const About = React.lazy(() => import('./pages/About'))
const NoodShop = React.lazy(() => import('./pages/NoodShop'))
const Workshops = lazyWithPreload(() => import('./pages/Workshops'))
const HostCourse = React.lazy(() => import('./pages/HostCourse'))
const Support = React.lazy(() => import('./pages/Support'))
const ClarityDetailsPage = React.lazy(() => import('./pages/clarity-details'))
const AccountabilityDetailsPage = React.lazy(() => import('./pages/accountability-details'))
interface OptimizedImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
}

export const OptimizedImage = ({
  src,
  alt,
  width,
  height,
  className
}: OptimizedImageProps) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <div
      className={`relative ${className}`}
      style={{ aspectRatio: width && height ? `${width}/${height}` : 'auto' }}
    >
      <LazyLoadImage
        src={src}
        alt={alt}
        width={width}
        height={height}
        effect="blur"
        afterLoad={() => setLoaded(true)}
        wrapperClassName={`transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        threshold={100}
      />
    </div>
  );
};

const App: React.FC = () => {
  // The entry chunk is deliberately small now, so the first navigation would
  // otherwise pay for its route chunk. Warm the handful of routes visitors
  // actually go to next, but only once the current page has finished loading.
  useEffect(() => {
    preloadWhenIdle([
      Courses.preload,
      Workshops.preload,
      CourseDetailPage.preload,
      Consultation.preload,
    ])
  }, [])

  return (
    <Router>
        <TrackFacebookPageViews />
        <DocumentTitle />
        <ScrollToTop />
        <div className="flex flex-col min-h-screen">
          <Header />
          <main className="flex-grow pt-24 sm:pt-10 w-full">
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/courses" element={<Courses />} />
                <Route path="/courses/:slug" element={<CourseDetailPage />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/community" element={<Community />} />
                <Route path="/community/clarity-details" element={<ClarityDetailsPage />} />
                <Route path="/community/accountability-details" element={<AccountabilityDetailsPage />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/consultation" element={<Consultation />} />
                <Route path="/tools" element={<NoodShop />} />
                <Route path="/workshops" element={<Workshops />} />
                <Route path="/workshops/whatsapp-ai-agent" element={<WhatsAppWorkshop />} />
                <Route path="/workshops/first-sale" element={<FirstSaleWorkshop />} />
                <Route path="/workshops/back-to-business" element={<BackToBusinessWorkshop />} />
                <Route path="/workshops/back-to-business-sept" element={<BackToBusinessWorkshopSept />} />
                <Route path="/workshops/:id" element={<WorkshopDetail />} />
                <Route path="/host-course" element={<HostCourse />} />
                <Route path="/support" element={<Support />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                <Route path="/products/:slug" element={<ProductDetail />} />
                <Route path="/courses/fashion-business-masterclass" element={<FashionBusinessMasterclass />} />
                <Route path="/courses/ecommerce-mastery" element={<EcommerceMastery />} />
                <Route path="/courses/english-mastery" element={<EnglishMastery />} />
                <Route path="/courses/ugc-creation-masterclass" element={<UGCCreationMasterclass />} />
                <Route path="/courses/ecommerce-mastery-virtual" element={<EcommerceMasteryVirtual />} />
                <Route path="/virtual-test-payment" element={<VirtualTestPayment />} />
                <Route path="/payment/success" element={<SuccessPage />} />
                <Route path="/payment/workshop/success" element={<WorkshopPaymentSuccess />} />
                <Route path="/payment/failure" element={<FailurePage />} />
                <Route path="/payment/cancel" element={<CancelPage />} />
                <Route path="/payment-test" element={<PaymentTest />} />
                <Route path="/test-payment" element={<TestPayment />} />
                <Route path="/form-test" element={<FormTest />} />
                <Route path="/contact" element={<ContactUs />} />
                <Route path="*" element={<NotFound />} />
                <Route path="/welcome" element={<Welcome />} />
                <Route path="/payment-required" element={<PaymentRequired />} />
                <Route path="/not-registered" element={<NotRegistered />} />
                <Route path="/register-clarity" element={<RegisterClarity />} />
                <Route path="/payment-test-page" element={<PaymentTestPage />} />
              </Routes>
              <Analytics />
            </Suspense>
          </main>
          <Footer />

          {/* LeadConnector Chat Widget */}
          <WhatsAppWidget
            autoOpen={true}
            autoOpenTimeout={1000}
            size="large"
            showOnMobile={true}
          />
        </div>
        <SpeedInsights />
      </Router>

  )
}

export default App
